import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, createElementBlock as _createElementBlock, Transition as _Transition, withCtx as _withCtx, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "relative space-y-6 lg:space-y-8 px-3 sm:px-4 lg:px-8 min-h-[calc(100vh-8rem)]" }
const _hoisted_2 = { class: "flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" }
const _hoisted_3 = { class: "flex items-center gap-2" }
const _hoisted_4 = { class: "hidden sm:inline" }
const _hoisted_5 = {
  key: 0,
  class: "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6"
}
const _hoisted_6 = {
  key: 0,
  class: "card border border-danger/20 dark:border-danger-dark/20 bg-danger/5 dark:bg-danger-dark/5 shadow-sm"
}
const _hoisted_7 = { class: "card-body" }
const _hoisted_8 = { class: "flex items-start space-x-3" }
const _hoisted_9 = { class: "flex-shrink-0" }
const _hoisted_10 = { class: "inline-flex items-center justify-center w-10 h-10 rounded-full bg-danger/10 dark:bg-danger-dark/10" }
const _hoisted_11 = { class: "flex-1" }
const _hoisted_12 = { class: "text-sm text-secondary-text/80 dark:text-secondary-dark-text/80" }
const _hoisted_13 = {
  key: 2,
  class: "card border border-gray-200/50 dark:border-gray-700/50 shadow-sm sticky bottom-0 z-30"
}
const _hoisted_14 = { class: "p-4" }
const _hoisted_15 = { class: "text-sm text-secondary-text/80 dark:text-secondary-dark-text/80" }
const _hoisted_16 = { class: "font-semibold text-slate-700 dark:text-slate-300" }
const _hoisted_17 = { class: "text-sm text-secondary-text/80 dark:text-secondary-dark-text/80" }
const _hoisted_18 = { class: "font-semibold text-slate-700 dark:text-slate-300" }
const _hoisted_19 = ["disabled"]

import { ref, computed, onMounted, watch } from 'vue'
import { useTitle } from '@/composables/ui/useTitle'
import { useToast } from 'vue-toastification'
import { useEmployees } from '@/composables/useEmployees'
import TableControls from '@/components/layout/TableControls.vue'
import EmployeeTable from '@/components/employees/EmployeeTable.vue'
import EmployeeModal from '@/components/employees/EmployeeModal.vue'
import EmployeeDetailsModal from '@/components/employees/EmployeeDetailsModal.vue'
import Modal from '@/components/layout/Modal.vue'
import StatCard from '@/components/layout/StatCard.vue'
import PaginationBar from '@/components/layout/PaginationBar.vue'
import type { Employee, CreateEmployeeData } from '@/services/employeeService'
import { employeeService } from '@/services/employeeService'
import { positionService, type Position } from '@/services/positionService'
import { UserRoundPlus, AlertCircle, Users, UserCheck, UserX, Clock, Eye, EyeOff, LoaderCircle } from 'lucide-vue-next'

const toastTimeout = 5000


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminEmployeesView',
  setup(__props) {

const appTitle = computed(() => process.env.VUE_APP_TITLE || 'PLANET Home Healthcare')
useTitle(`${appTitle.value} - Employees`)

const toast = useToast()
const {
	employees,
	loading,
	error,
	stats,
	fetchEmployees,
	createEmployee,
	updateEmployee,
	toggleEmployeeStatus,
	deleteEmployee,
	clearError
} = useEmployees()

const showStats = ref(false)
const showDetailsModal = ref(false)
const detailsLoading = ref(false)
const selectedEmployeeForDetails = ref<Employee | null>(null)
const showModal = ref(false)
const showCreateModal = ref(false)
const createValidationErrors = ref<Record<string, string>>({})
const selectedEmployee = ref<Employee | null>(null)
const editMode = ref(false)
const positions = ref<Position[]>([])
const showDeleteConfirm = ref(false)
const pendingDeleteId = ref<number | null>(null)
const pendingDeleteName = computed(() => {
  if (pendingDeleteId.value === null) return 'this employee'

  const employee = employees.value.find((item) => item.id === pendingDeleteId.value)
  if (!employee) return 'this employee'

  if (employee.first_name && employee.last_name) {
    return `${employee.first_name} ${employee.last_name}`
  }

  return employee.name || employee.username || 'this employee'
})

const confirmActionType = ref<'approve' | 'activate'>('approve')
const showApproveConfirm = ref(false)
const pendingApproveId = ref<number | null>(null)
const pendingApproveName = computed(() => {
  if (pendingApproveId.value === null) return 'this employee'

  const employee = employees.value.find((item) => item.id === pendingApproveId.value)
  if (!employee) return 'this employee'

  if (employee.first_name && employee.last_name) {
    return `${employee.first_name} ${employee.last_name}`
  }

  return employee.name || employee.username || 'this employee'
})

const statusOptions = [
	{ value: 'all', label: 'All Status' },
	{ value: 'active', label: 'Active' },
	{ value: 'inactive', label: 'Inactive' },
	{ value: 'pending', label: 'Pending' }
]

// Status filter (client-side)
const statusFilter = ref<'all' | 'active' | 'inactive' | 'pending'>('all')
const statusFiltered = computed(() => {
	const list = employees.value || []
	if (statusFilter.value === 'active') return list.filter((e) => e.status === 'active')
	if (statusFilter.value === 'inactive') return list.filter((e) => e.status === 'inactive')
	if (statusFilter.value === 'pending') return list.filter((e) => e.status === 'pending')
	return list
})

// Search filter (client-side) - searches across multiple fields
const searchQuery = ref('')
const searchFiltered = computed(() => {
	const list = statusFiltered.value
	if (!searchQuery.value.trim()) return list
	
	const query = searchQuery.value.toLowerCase()
	return list.filter((e) => {
		const name = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase()
		const username = (e.username || '').toLowerCase()
		const email = (e.email || '').toLowerCase()
		return name.includes(query) || username.includes(query) || email.includes(query)
	})
})

// Pagination (client-side)
const perPage = ref(10)
const currentPage = ref(1)
const filteredTotalPages = computed(() => Math.max(1, Math.ceil(searchFiltered.value.length / perPage.value)))
const filteredTotal = computed(() => searchFiltered.value.length)

const paginatedEmployees = computed(() => {
	const start = (currentPage.value - 1) * perPage.value
	const end = start + perPage.value
	return searchFiltered.value.slice(start, end)
})

// Reset page when filters change
watch([statusFilter, searchQuery, perPage], () => {
  	currentPage.value = 1
})

const goToPage = (page: number) => {
  	currentPage.value = page
}

const handleView = async (id: number) => {
	try {
		detailsLoading.value = true
		showDetailsModal.value = true
		const employee = await employeeService.getEmployee(id)
		selectedEmployeeForDetails.value = employee
	} catch (err) {
		console.error('Failed to fetch employee:', err)
		showDetailsModal.value = false
	} finally {
		detailsLoading.value = false
	}
}

const closeDetailsModal = () => {
	showDetailsModal.value = false
	selectedEmployeeForDetails.value = null
}

const handleEditFromDetails = () => {
	const employeeToEdit = selectedEmployeeForDetails.value
	closeDetailsModal()
	selectedEmployee.value = employeeToEdit
	editMode.value = true
	showModal.value = true
}

const handleEdit = () => {
  	editMode.value = true
}


const handleApprove = (id: number) => {
	confirmActionType.value = 'approve'
	pendingApproveId.value = id
	showApproveConfirm.value = true
}

const handleActivate = (id: number) => {
	confirmActionType.value = 'activate'
	pendingApproveId.value = id
	showApproveConfirm.value = true
}

const confirmApprove = async () => {
	if (pendingApproveId.value === null) return
	
	const id = pendingApproveId.value
	
	try {
		await toggleEmployeeStatus(id, 'active')
		// Close modal before refresh to prevent rendering conflicts
		closeModalOrCreateModal()
		
		toast.success('Employee activated successfully', { timeout: toastTimeout })
		await fetchEmployees()
	} catch (err: any) {
		const errorMessage = err?.response?.data?.message || 'Failed to activate employee'
		toast.error(errorMessage, { timeout: toastTimeout })
		console.error('Failed to activate employee:', err)
	} finally {
		showApproveConfirm.value = false
		pendingApproveId.value = null
	}
}

const cancelApprove = () => {
	showApproveConfirm.value = false
	pendingApproveId.value = null
}

const handleDeactivate = async (id: number) => {
	try {
		await toggleEmployeeStatus(id, 'inactive')
		// Close modal before refresh to prevent rendering conflicts
		closeModalOrCreateModal()
		
		toast.success('Employee deactivated successfully', { timeout: toastTimeout })
		await fetchEmployees()
	} catch (err: any) {
		const errorMessage = err?.response?.data?.message || 'Failed to deactivate employee'
		toast.error(errorMessage, { timeout: toastTimeout })
		console.error('Failed to deactivate employee:', err)
	}
}

const handleDelete = (id: number) => {
	pendingDeleteId.value = id
	showDeleteConfirm.value = true
}

const confirmDelete = async () => {
	if (pendingDeleteId.value === null) return

	const id = pendingDeleteId.value

	try {
		await deleteEmployee(id)
		// Close modal before refresh to prevent rendering conflicts
		closeModalOrCreateModal()
		
		toast.success('Employee deleted successfully', { timeout: toastTimeout })
		await fetchEmployees()
	} catch (err: any) {
		const errorMessage = err?.response?.data?.message || 'Failed to delete employee'
		toast.error(errorMessage, { timeout: toastTimeout })
		console.error('Failed to delete employee:', err)
	} finally {
		showDeleteConfirm.value = false
		pendingDeleteId.value = null
	}
}

const cancelDelete = () => {
	showDeleteConfirm.value = false
	pendingDeleteId.value = null
}

const closeModalOrCreateModal = () => {
	showModal.value = false
	showCreateModal.value = false
	selectedEmployee.value = null
	editMode.value = false
	createValidationErrors.value = {}
	clearError()
	closeDetailsModal()
}

const openCreateModal = () => {
	selectedEmployee.value = null
	editMode.value = false
	createValidationErrors.value = {}
	clearError()
	showCreateModal.value = true
}

const handleSave = async (data: CreateEmployeeData & { password_confirmation?: string }) => {
	createValidationErrors.value = {}
	clearError()

	try {
		// Map frontend field names to backend field names
		const apiData: any = { ...data }
		if (apiData.street !== undefined) {
			apiData.address = apiData.street
			delete apiData.street
		}
		if (apiData.position_id !== undefined) {
			apiData.position = apiData.position_id
			delete apiData.position_id
		}

		await createEmployee(apiData)

		const employeeName = data.first_name && data.last_name
		? `${data.first_name} ${data.last_name}`
		: 'Employee'
		toast.success(`Employee "${employeeName}" has been created successfully!`, { timeout: toastTimeout })

		await fetchEmployees()
		showCreateModal.value = false
	} catch (error: any) {
		const backendErrors = error?.response?.data?.errors

		if (backendErrors && typeof backendErrors === 'object') {
			const mappedErrors: Record<string, string> = {}

			Object.entries(backendErrors).forEach(([field, value]) => {
				if (Array.isArray(value) && value.length > 0) {
				mappedErrors[field] = String(value[0])
				} else if (value) {
				mappedErrors[field] = String(value)
				}
			})

			createValidationErrors.value = mappedErrors
			clearError()
			return
		}

		const errorMessage = error?.response?.data?.message ||
		error?.response?.data?.error ||
		error?.message ||
		'Failed to create employee. Please try again.'

		toast.error(errorMessage, { timeout: toastTimeout })
		console.error('Error creating employee:', error)
	}
}
const handleUpdate = async (id: number, data: any) => {
	try {
		// Validate required fields
		if (!data.first_name || !data.last_name || !data.username || !data.email) {
			toast.error('Please fill in all required fields (First Name, Last Name, Username, Email)', { timeout: toastTimeout })
			return
		}

		// Validate password if provided
		if (data.password) {
			if (data.password.length < 8) {
				toast.error('Password must be at least 8 characters long', { timeout: toastTimeout })
				return
			}

			// Validate password confirmation
			if (data.password_confirmation && data.password !== data.password_confirmation) {
				toast.error('Passwords do not match', { timeout: toastTimeout })
				return
			}
		}

		// Validate email format
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
			if (!emailRegex.test(data.email)) {
			toast.error('Please enter a valid email address', { timeout: toastTimeout }	)
			return
		}

		// Call the updateEmployee function from the composable
		await updateEmployee(id, data);

		// Show success toast
		const employeeName = data.first_name && data.last_name
		? `${data.first_name} ${data.last_name}`
		: 'Employee'
		toast.success(`Employee "${employeeName}" has been updated successfully!`, { timeout: toastTimeout })

		// Refresh the employee list
		await fetchEmployees();

		// Close the modal and reset
		showModal.value = false
		selectedEmployee.value = null
		editMode.value = false
	} catch (error: any) {
		// Extract error message from API response
		const errorMessage = error?.response?.data?.message ||
		error?.response?.data?.error ||
		error?.message ||
		'Failed to update employee. Please try again.'

		// Show validation errors from backend
		if (error?.response?.data?.errors) {
			const errors = error.response.data.errors
			const firstError = Object.values(errors)[0]
			const errorMsg = Array.isArray(firstError) ? firstError[0] : firstError
			toast.error(errorMsg as string, { timeout: toastTimeout })
		} else {
			toast.error(errorMessage, { timeout: toastTimeout })
		}
		console.error('Error updating employee:', error);
	}
}

onMounted(async () => {
	try {
		positions.value = await positionService.getPositions()
		await fetchEmployees()
	} catch (err) {
		console.error('Failed to load initial data:', err)
	}
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[7] || (_cache[7] = _createElementVNode("div", null, [
        _createElementVNode("h1", { class: "text-2xl sm:text-3xl lg:text-4xl font-bold text-primary dark:secondary" }, " Employees "),
        _createElementVNode("p", { class: "text-sm text-slate-500 dark:text-slate-200 mt-2" }, " Manage and monitor all employee accounts ")
      ], -1)),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (showStats.value = !showStats.value)),
          class: "px-4 py-2 rounded-lg font-semibold transition border border-gray-300 dark:border-gray-600 text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-700 inline-flex items-center justify-center w-full sm:w-auto"
        }, [
          (!showStats.value)
            ? (_openBlock(), _createBlock(_unref(Eye), {
                key: 0,
                class: "h-5 w-5 mr-2"
              }))
            : (_openBlock(), _createBlock(_unref(EyeOff), {
                key: 1,
                class: "h-5 w-5 mr-2"
              })),
          _createElementVNode("span", _hoisted_4, _toDisplayString(showStats.value ? 'Hide Stats' : 'Show Stats'), 1)
        ]),
        _createElementVNode("button", {
          onClick: openCreateModal,
          class: "px-6 py-2 rounded-lg font-semibold text-white transition bg-primary hover:bg-secondary shadow-md hover:opacity-90 dark:bg-gradient-to-tr dark:from-primary/80 dark:to-secondary/80 dark:shadow-none dark:ring-1 dark:ring-white/10 inline-flex items-center justify-center w-full sm:w-auto"
        }, [
          _createVNode(_unref(UserRoundPlus), { class: "h-5 w-5 mr-2" }),
          _cache[6] || (_cache[6] = _createElementVNode("span", { class: "hidden sm:inline" }, "Add New Employee", -1))
        ])
      ])
    ]),
    _createVNode(_Transition, { name: "stats" }, {
      default: _withCtx(() => [
        (showStats.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createVNode(StatCard, {
                label: "Total Employees",
                value: _unref(stats).total,
                loading: _unref(loading),
                icon: _unref(Users),
                color: " text-blue-600  dark:text-blue-400"
              }, null, 8, ["value", "loading", "icon"]),
              _createVNode(StatCard, {
                label: "Active",
                value: _unref(stats).active,
                loading: _unref(loading),
                icon: _unref(UserCheck),
                color: "text-emerald-600 dark:text-emerald-400"
              }, null, 8, ["value", "loading", "icon"]),
              _createVNode(StatCard, {
                label: "Inactive",
                value: _unref(stats).inactive,
                loading: _unref(loading),
                icon: _unref(UserX),
                color: "text-rose-600 dark:text-rose-400"
              }, null, 8, ["value", "loading", "icon"]),
              _createVNode(StatCard, {
                label: "Pending",
                value: _unref(stats).pending,
                loading: _unref(loading),
                icon: _unref(Clock),
                color: "text-amber-600 dark:text-amber-400"
              }, null, 8, ["value", "loading", "icon"])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createVNode(TableControls, {
      search: searchQuery.value,
      "onUpdate:search": _cache[1] || (_cache[1] = ($event: any) => ((searchQuery).value = $event)),
      status: statusFilter.value,
      "onUpdate:status": _cache[2] || (_cache[2] = ($event: any) => ((statusFilter).value = $event)),
      perPage: perPage.value,
      "onUpdate:perPage": _cache[3] || (_cache[3] = ($event: any) => ((perPage).value = $event)),
      options: statusOptions,
      filterLabel: "Status",
      placeholder: "Search by name, username or email..."
    }, null, 8, ["search", "status", "perPage"]),
    (_unref(error))
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(_unref(AlertCircle), { class: "h-5 w-5 text-danger dark:text-danger-dark" })
                ])
              ]),
              _createElementVNode("div", _hoisted_11, [
                _cache[8] || (_cache[8] = _createElementVNode("h3", { class: "text-sm font-semibold text-danger dark:text-danger-dark mb-1" }, " Error Loading Employees ", -1)),
                _createElementVNode("p", _hoisted_12, _toDisplayString(_unref(error)), 1)
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    (!_unref(error))
      ? (_openBlock(), _createBlock(EmployeeTable, {
          key: 1,
          employees: paginatedEmployees.value,
          loading: _unref(loading),
          onView: handleView,
          onActivate: handleActivate,
          onApprove: handleApprove,
          onDeactivate: handleDeactivate,
          onDelete: handleDelete
        }, null, 8, ["employees", "loading"]))
      : _createCommentVNode("", true),
    (!_unref(loading) && !_unref(error) && filteredTotal.value > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
          _createElementVNode("div", _hoisted_14, [
            _createVNode(PaginationBar, {
              "current-page": currentPage.value,
              "total-pages": filteredTotalPages.value,
              total: filteredTotal.value,
              "per-page": perPage.value,
              "onUpdate:page": goToPage
            }, null, 8, ["current-page", "total-pages", "total", "per-page"])
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(EmployeeDetailsModal, {
      "is-open": showDetailsModal.value,
      employee: selectedEmployeeForDetails.value,
      loading: detailsLoading.value,
      onClose: closeDetailsModal,
      onEdit: handleEditFromDetails
    }, null, 8, ["is-open", "employee", "loading"]),
    _createVNode(EmployeeModal, {
      "is-open": showModal.value || showCreateModal.value,
      employee: selectedEmployee.value,
      "edit-mode": editMode.value,
      "validation-errors": createValidationErrors.value,
      "existing-employees": _unref(employees),
      positions: positions.value,
      loading: _unref(loading),
      onClose: closeModalOrCreateModal,
      onSave: handleSave,
      onUpdate: handleUpdate,
      onEdit: handleEdit
    }, null, 8, ["is-open", "employee", "edit-mode", "validation-errors", "existing-employees", "positions", "loading"]),
    _createVNode(Modal, {
      modelValue: showDeleteConfirm.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showDeleteConfirm).value = $event)),
      title: "Delete Employee"
    }, {
      footer: _withCtx(() => [
        _createElementVNode("button", {
          type: "button",
          class: "px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200",
          onClick: cancelDelete
        }, " Cancel "),
        _createElementVNode("button", {
          type: "button",
          class: "ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-danger hover:bg-danger/90 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed",
          onClick: confirmDelete
        }, " Yes, delete permanently ")
      ]),
      default: _withCtx(() => [
        _createElementVNode("p", _hoisted_15, [
          _cache[9] || (_cache[9] = _createTextVNode(" Are you sure you want to permanently delete ", -1)),
          _createElementVNode("span", _hoisted_16, "\"" + _toDisplayString(pendingDeleteName.value) + "\"", 1),
          _cache[10] || (_cache[10] = _createTextVNode("? This action cannot be undone. ", -1))
        ])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(Modal, {
      modelValue: showApproveConfirm.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showApproveConfirm).value = $event)),
      title: confirmActionType.value === 'approve' ? 'Approve Employee' : 'Activate Employee'
    }, {
      footer: _withCtx(() => [
        _createElementVNode("button", {
          type: "button",
          class: "px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-secondary-text dark:text-secondary-dark-text hover:bg-gray-100/80 dark:hover:bg-gray-700/60 transition-colors duration-200",
          onClick: cancelApprove
        }, " Cancel "),
        _createElementVNode("button", {
          type: "button",
          class: "flex justify-center ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-success hover:bg-success/90 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed",
          onClick: confirmApprove,
          disabled: _unref(loading)
        }, [
          (_unref(loading))
            ? (_openBlock(), _createBlock(_unref(LoaderCircle), {
                key: 0,
                class: "w-5 h-5 mr-2 animate-spin"
              }))
            : _createCommentVNode("", true),
          _createTextVNode(" " + _toDisplayString(_unref(loading) ? 'Processing...' : `Yes, ${confirmActionType.value === 'approve' ? 'approve and activate' : 'reactivate'}`), 1)
        ], 8, _hoisted_19)
      ]),
      default: _withCtx(() => [
        _createElementVNode("p", _hoisted_17, [
          _createTextVNode(" Are you sure you want to " + _toDisplayString(confirmActionType.value === 'approve' ? 'approve and activate' : 'reactivate') + " ", 1),
          _createElementVNode("span", _hoisted_18, "\"" + _toDisplayString(pendingApproveName.value) + "\"", 1),
          _cache[11] || (_cache[11] = _createTextVNode("? ", -1))
        ])
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ]))
}
}

})